/**
 * InlineTemplateCreator
 *
 * Modal compacto para crear una plantilla WhatsApp sin salir del wizard.
 *
 * Usa POST /whatsapp/templates/mock (aprobada directo, sin Meta).
 * UX simétrica a InlineSegmentCreator: describí lo que querés decir,
 * la "IA" arma el body con preview, le ponés un nombre y queda lista
 * para usar.
 */

import { useState } from "react";
import {
    Sparkles,
    X,
    Loader2,
    AlertCircle,
    CheckCircle2,
    FileText,
} from "lucide-react";
import { whatsappApi } from "../../whatsapp/api";
import type { WhatsAppTemplate } from "../../whatsapp/types";

interface InlineTemplateCreatorProps {
    open: boolean;
    brandId: string;
    onClose: () => void;
    onCreated: (template: WhatsAppTemplate) => void;
}

const EXAMPLE_PROMPTS = [
    "Tenemos 30% off en toda la tienda hasta fin de semana",
    "Tu pedido está demorado, te enviamos un cupón de disculpa",
    "Volvé después de 30 días sin compras y te regalamos $500",
    "Recordatorio de carrito abandonado con un beneficio",
];

/**
 * Genera un body de preview localmente para que el usuario vea cómo va
 * a quedar el mensaje antes de guardar. Idéntico a la lógica del backend
 * para evitar sorpresas tras el POST.
 */
function buildPreviewBody(description: string): string {
    const desc = description.trim();
    if (!desc) return "";
    return `Hola {{1}}, ${desc.charAt(0).toLowerCase()}${desc.slice(1)}.\n\nNo te lo pierdas. 🎉`;
}

export function InlineTemplateCreator({
    open,
    brandId,
    onClose,
    onCreated,
}: InlineTemplateCreatorProps) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<string | null>(null);

    if (!open) return null;

    const handleClose = () => {
        if (pending) return;
        setName("");
        setDescription("");
        setPreview(null);
        setError(null);
        onClose();
    };

    const handlePreview = async () => {
        if (!description.trim()) {
            setError("Describí qué querés que diga el mensaje");
            return;
        }
        setError(null);
        setPending(true);
        // Latencia simulada
        await new Promise((r) => setTimeout(r, 700));
        setPreview(buildPreviewBody(description));
        setPending(false);
    };

    const handleConfirm = async () => {
        if (!preview) {
            setError("Primero generá el mensaje");
            return;
        }
        if (!name.trim()) {
            setError("Ponele un nombre a la plantilla");
            return;
        }
        setError(null);
        setPending(true);
        try {
            const res = await whatsappApi.createMockTemplate({
                brandId,
                name: name.trim(),
                description: description.trim(),
            });
            if (res.success && res.data) {
                onCreated(res.data);
                setName("");
                setDescription("");
                setPreview(null);
            } else {
                setError(res.error || "Error al guardar la plantilla");
            }
        } catch (err: any) {
            setError(
                err?.response?.data?.error ||
                    err.message ||
                    "Error inesperado",
            );
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                            <FileText size={16} className="text-white" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">
                                Crear plantilla
                            </h3>
                            <p className="text-xs text-slate-500">
                                Describí qué querés decir y armamos el mensaje
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={pending}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto space-y-5">
                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-900 mb-2">
                            ¿Qué querés que diga?
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={pending || !!preview}
                            placeholder="Ej: tenemos 30% off en toda la tienda hasta fin de semana"
                            rows={3}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 resize-none
                                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all
                                disabled:bg-slate-50 disabled:cursor-not-allowed"
                        />
                        {!preview && (
                            <div className="mt-2">
                                <p className="text-[10px] text-slate-500 mb-1.5">
                                    💡 Inspiración:
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {EXAMPLE_PROMPTS.map((ex) => (
                                        <button
                                            key={ex}
                                            type="button"
                                            onClick={() => setDescription(ex)}
                                            disabled={pending}
                                            className="text-[10px] px-2 py-1 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-700 border border-slate-100 hover:border-emerald-200 rounded-md transition-all"
                                        >
                                            {ex}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    {preview && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 animate-fade-in">
                            <div className="flex items-start gap-2 mb-3">
                                <CheckCircle2
                                    size={16}
                                    className="text-emerald-600 mt-0.5 flex-shrink-0"
                                />
                                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                                    Mensaje armado por la IA
                                </p>
                            </div>
                            <div className="bg-white border border-emerald-100 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {preview}
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2">
                                {"{{1}}"} se reemplaza con el nombre del cliente
                                al enviar
                            </p>
                        </div>
                    )}

                    {/* Name */}
                    {preview && (
                        <div className="animate-fade-in">
                            <label className="block text-sm font-semibold text-slate-900 mb-2">
                                Ponele un nombre interno
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value.slice(0, 80))}
                                placeholder="Ej: Promo Verano"
                                maxLength={80}
                                autoFocus
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                                    focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all"
                            />
                            <p className="text-[10px] text-slate-400 mt-1 text-right font-mono">
                                {name.length}/80
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl text-red-700 animate-fade-in">
                            <AlertCircle size={16} />
                            <span className="text-xs font-medium">{error}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 flex-shrink-0">
                    <button
                        onClick={handleClose}
                        disabled={pending}
                        className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
                    >
                        Cancelar
                    </button>

                    {!preview ? (
                        <button
                            onClick={handlePreview}
                            disabled={pending || !description.trim()}
                            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
                                ${
                                    pending || !description.trim()
                                        ? "bg-emerald-300 cursor-not-allowed"
                                        : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg shadow-emerald-500/25 active:scale-[0.98]"
                                }`}
                        >
                            {pending ? (
                                <>
                                    <Loader2 size={15} className="animate-spin" />
                                    Armando…
                                </>
                            ) : (
                                <>
                                    <Sparkles size={15} />
                                    Generar mensaje
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={handleConfirm}
                            disabled={pending || !name.trim()}
                            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 text-white
                                ${
                                    pending || !name.trim()
                                        ? "bg-emerald-300 cursor-not-allowed"
                                        : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/25 active:scale-[0.98]"
                                }`}
                        >
                            {pending ? (
                                <>
                                    <Loader2 size={15} className="animate-spin" />
                                    Guardando…
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 size={15} />
                                    Crear y usar esta plantilla
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default InlineTemplateCreator;
